import React from 'react';
import {
    openFinanceApi,
    getOpenFinanceCompanies,
    getBankStatementPreview,
    syncBankStatement,
    setupIntegration,
    updateTransactionCategory,
    deleteBankTransaction,
    deleteIntegrationSetup
} from '../../../../../api/integrations';

import {
    listAccounts,
    createAccount,
    deleteAccount
} from '../../../../../api/openFinanceTransactions';

const getLocalDateString = (d) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const normalizeDate = (dateStr) => {
    if (!dateStr) return "";
    const parts = dateStr.split("-");
    if (parts.length !== 3) return dateStr;
    const year = parts[0];
    const month = parts[1].padStart(2, "0");
    const day = parts[2].padStart(2, "0");
    return `${year}-${month}-${day}`;
};

const getStoredBranchInfo = () => {
    if (typeof window === 'undefined') {
        return { id: '', name: '' };
    }

    try {
        const cachedBranch = window.localStorage.getItem('easygestorweb_current_branch');
        if (cachedBranch) {
            const parsed = JSON.parse(cachedBranch);
            if (parsed && parsed.id) {
                return {
                    id: parsed.id,
                    name: parsed.name || (parsed.company && parsed.company.fancy_name) || ''
                };
            }
        }
    } catch (err) {
        console.debug('OpenFinance: failed to parse cached branch info', err);
    }

    const params = new URLSearchParams(window.location.search || '');
    const queryBranch = params.get('branch_owner');
    if (queryBranch) {
        return { id: queryBranch, name: '' };
    }

    if (typeof document !== 'undefined') {
        const cookieEntry = document.cookie
            .split(';')
            .map(c => c.trim())
            .find(c => c.startsWith('branch_owner_id='));
        if (cookieEntry) {
            const [, value] = cookieEntry.split('=');
            return { id: decodeURIComponent(value), name: '' };
        }
    }

    return { id: '', name: '' };
};

export function useOpenFinance(context) {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);

    const storedBranch = React.useMemo(() => getStoredBranchInfo(), []);
    const storedBranchId = storedBranch.id;
    const storedBranchName = storedBranch.name;

    const [loading, setLoading] = React.useState(false);
    const [transactions, setTransactions] = React.useState([]);
    const [companies, setCompanies] = React.useState(() => {
        if (storedBranchId) {
            return [{ id: storedBranchId, name: storedBranchName || 'Minha Empresa' }];
        }
        return [];
    });
    const [selectedCompanyId, setSelectedCompanyId] = React.useState(storedBranchId || '');
    const [dateFrom, setDateFrom] = React.useState(getLocalDateString(firstDay));
    const [dateTo, setDateTo] = React.useState(getLocalDateString(today));
    const [error, setError] = React.useState(null);
    const [selectedProvider, setSelectedProvider] = React.useState('inter');

    const [previewData, setPreviewData] = React.useState(null);
    const [needsSetup, setNeedsSetup] = React.useState(false);
    const [bankCategories, setBankCategories] = React.useState([]);
    const [credentials, setCredentials] = React.useState({
        clientId: "",
        clientSecret: "",
        fileCrt: null,
        fileKey: null,
        isConfigured: false
    });

    const branchIdFromContext = (context && context.currentBranch && context.currentBranch.id) || '';
    const branchNameFromContext = (context && context.currentBranch && (context.currentBranch.name || (context.currentBranch.company && context.currentBranch.company.fancy_name))) || '';
    const getCompanyId = () => selectedCompanyId || branchIdFromContext || storedBranchId || '';
    const effectiveCompanyId = getCompanyId();
    const companyDisplayName = branchNameFromContext || storedBranchName || 'Minha Empresa';

    const fetchCategories = async () => {
        try {
            // openFinanceTransactions automatically sends branch_owner_id in headers via interceptors
            const res = await listAccounts();
            const data = res.data || res;
            setBankCategories(data);
        } catch (err) {
            console.debug('OpenFinance: failed to load categories', err);
        }
    };

    const fetchCredentials = async (companyId, provider) => {
        try {
            const targetCompany = companyId || getCompanyId();
            if (!targetCompany) return;
            const effectiveProvider = provider || selectedProvider;
            const res = await openFinanceApi.get(`/companies/${targetCompany}/credentials/`);
            const data = res.data || [];
            const cred = data.find(c => c.provider === effectiveProvider);
            if (cred) {
                setCredentials({
                    clientId: cred.client_id || "",
                    clientSecret: "********", // Mascarado
                    fileCrt: cred.has_cert ? { name: "Certificado já enviado" } : null,
                    fileKey: cred.has_key ? { name: "Chave privada já enviada" } : null,
                    isConfigured: true
                });
                setNeedsSetup(false);
            } else {
                setCredentials({ clientId: "", clientSecret: "", fileCrt: null, fileKey: null, isConfigured: false });
                setNeedsSetup(true);
            }
        } catch (err) {
            console.debug('OpenFinance: failed to load credentials', err);
        }
    };

    React.useEffect(() => {
        // SEGURANÇA: nunca listar todas as empresas — usar apenas o branch_owner_id do usuário logado.
        // getOpenFinanceCompanies() retornava TODAS as empresas do banco, expondo dados sensíveis.
        if (!branchIdFromContext) return;

        setCompanies([{ id: branchIdFromContext, name: companyDisplayName }]);
        setSelectedCompanyId(branchIdFromContext);
    }, [branchIdFromContext, companyDisplayName]);

    const fetchTransactions = async (companyId) => {
        const targetCompany = companyId || getCompanyId();
        if (!targetCompany) return;
        setLoading(true);
        setError(null);
        try {
            const res = await openFinanceApi.get(
                `/companies/${targetCompany}/bank/transactions`
            );
            const data = res.data || [];
            setTransactions(data);
        } catch (err) {
            console.error('Fetch error:', err);
            setError('Erro ao carregar transações do histórico.');
            setTransactions([]);
        } finally {
            setLoading(false);
        }
    };

    const handlePreview = async () => {
        const companyId = getCompanyId();
        if (!companyId || !dateFrom || !dateTo) {
            setError("Selecione uma filial e o período para buscar o extrato.");
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const response = await getBankStatementPreview({
                date_from: normalizeDate(dateFrom),
                date_to: normalizeDate(dateTo),
                provider: selectedProvider
            }, { params: { branch_owner: companyId } });

            const data = response.data || response;
            setPreviewData(data);
        } catch (err) {
            const status = err.response ? err.response.status : null;
            if (status === 404) {
                setNeedsSetup(true);
            } else {
                setError('Erro ao buscar preview do banco. Verifique as credenciais.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleSync = async () => {
        const companyId = getCompanyId();
        if (!companyId || !dateFrom || !dateTo) {
            setError("Selecione uma filial e o período para sincronizar o extrato.");
            return;
        }
        setLoading(true);
        setError(null);
        try {
            await syncBankStatement({
                date_from: normalizeDate(dateFrom),
                date_to: normalizeDate(dateTo),
                provider: selectedProvider
            }, { params: { branch_owner: companyId } });

            setPreviewData(null);
            fetchTransactions(companyId);
        } catch (err) {
            setError('Erro ao sincronizar extrato bancário.');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateCategory = async (name, parentId = null) => {
        try {
            await createAccount({ name, parent_id: parentId, code: name.slice(0, 10).toUpperCase(), type: 'expense', description: name });
            await fetchCategories();
        } catch (e) {
            setError("Erro ao criar categoria.");
        }
    };

    const handleDeleteCategory = async (categoryId) => {
        try {
            await deleteAccount(categoryId);
            await fetchCategories();
        } catch (e) {
            setError("Erro ao excluir categoria (pode haver transações nela).");
        }
    };

    const handleUpdateCategory = async (transactionId, categoryId) => {
        const companyId = getCompanyId();
        if (!companyId) return;
        try {
            // Se categoryId for vazio, enviamos null (ou endpoint aceita null)
            const catId = categoryId === "" ? null : categoryId;
            await updateTransactionCategory(companyId, transactionId, { category_id: catId });

            // Atualiza localmente para refletir na tabela instantaneamente
            setTransactions(prev => prev.map(t => t.id === transactionId ? { ...t, category_id: catId } : t));
        } catch (e) {
            setError("Erro ao atualizar categoria da transação.");
        }
    };

    const handleDeleteTransaction = async (transactionId) => {
        const companyId = getCompanyId();
        if (!companyId) return;
        try {
            await deleteBankTransaction(companyId, transactionId);
            setTransactions(prev => prev.filter(t => t.id !== transactionId));
        } catch (e) {
            setError("Erro ao excluir transação.");
        }
    };

    const readFileContent = (file) => {
        return new Promise((resolve, reject) => {
            if (!file) return resolve("");
            const reader = new FileReader();
            reader.onload = (e) => resolve(e.target.result);
            reader.onerror = (e) => reject(e);
            reader.readAsText(file);
        });
    };

    const handleSetup = async () => {
        const effectiveId = getCompanyId();

        // Verifica se o banco selecionado precisa de certificado mTLS
        const PROVIDERS_NEEDING_CERT = ['inter'];
        const needsCert = PROVIDERS_NEEDING_CERT.includes(selectedProvider);

        const missingCert = needsCert && (!credentials.fileCrt || !credentials.fileKey);
        if (!effectiveId || !credentials.clientId || !credentials.clientSecret || missingCert) {
            setError(needsCert
                ? "Preencha todos os campos e anexe os arquivos de certificado."
                : "Preencha o Client ID e o Client Secret."
            );
            return;
        }
        setLoading(true);
        try {
            let crtContent = null;
            let keyContent = null;

            if (needsCert) {
                const isCrtPlaceholder = credentials.fileCrt && credentials.fileCrt.name === "Certificado já enviado";
                const isKeyPlaceholder = credentials.fileKey && credentials.fileKey.name === "Chave privada já enviada";
                crtContent = isCrtPlaceholder ? null : await readFileContent(credentials.fileCrt);
                keyContent = isKeyPlaceholder ? null : await readFileContent(credentials.fileKey);
            }

            const payload = {
                provider: selectedProvider,
                client_id: credentials.clientId,
                client_secret: credentials.clientSecret === "********" ? null : credentials.clientSecret,
                cert_content: crtContent,
                key_content: keyContent
            };

            await setupIntegration(payload, { params: { branch_owner: effectiveId } });
            setNeedsSetup(false);

            // Recarrega credenciais para refletir estado atualizado
            fetchCredentials(effectiveId, selectedProvider);
            handlePreview();
        } catch (err) {
            setError("Erro ao configurar integração. Verifique as credenciais.");
        } finally {
            setLoading(false);
        }
    };


    const handleDeleteSetup = async () => {
        const effectiveId = getCompanyId();

        if (!effectiveId) return;

        setLoading(true);
        try {
            await deleteIntegrationSetup(selectedProvider, { params: { branch_owner: effectiveId } });
            setCredentials({ clientId: "", clientSecret: "", fileCrt: null, fileKey: null, isConfigured: false });
            setNeedsSetup(true);
        } catch (err) {
            setError("Erro ao excluir configuração.");
        } finally {
            setLoading(false);
        }
    };

    React.useEffect(() => {
        if (effectiveCompanyId) {
            fetchTransactions(effectiveCompanyId);
            fetchCategories();
            fetchCredentials(effectiveCompanyId, selectedProvider);
        }
    }, [effectiveCompanyId, selectedProvider]);

    return {
        loading,
        transactions,
        previewData,
        setPreviewData,
        companies,
        selectedCompanyId: effectiveCompanyId,
        setSelectedCompanyId,
        dateFrom,
        setDateFrom,
        dateTo,
        setDateTo,
        selectedProvider,
        setSelectedProvider,
        error,
        setError,
        fetchTransactions,
        handlePreview,
        handleSync,
        needsSetup,
        setNeedsSetup,
        credentials,
        setCredentials,
        handleSetup,
        bankCategories,
        fetchCategories,
        handleCreateCategory,
        handleDeleteCategory,
        handleUpdateCategory,
        handleDeleteTransaction,
        handleDeleteSetup
    };
}



// WEBPACK FOOTER //
// ./src/views/AppViews/Financial/OpenFinance/hooks/useOpenFinance.js